// i18n area 'cluster' (task #127): sections cluster, clusterDevices, settings, localSettings.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

import type { PluralForms } from '../plural'

// Helper locale al file, identico a plural() in ../plural: un import RUNTIME
// da una sezione romperebbe scripts/i18n-status.mjs, che carica questi file
// con node (type stripping, niente resolver TS) — l'import type si cancella,
// uno runtime no. Il tipo resta l'unico e vero contratto.
const plural = (forms: PluralForms): PluralForms => forms

export const enUS = {
  cluster: {
    // #233 — the service accesses of a node (R4, T233-P3-bis): the read-only
    // grant (password generated by the backend, shown ONCE), its record on the
    // node and the emergency account's last login from the journal.
    nodeAccess: {
      title: 'Service accesses',
      subtitle: 'The node’s service door: the read-only access with a temporary password and the emergency account with the vault password',
      readOnlyTitle: 'Read-only access (trantor)',
      stateNone: 'closed',
      stateGranted: 'open',
      stateGrantedPending: 'granted — being applied on the node',
      grantedBy: 'Granted by',
      grantedAt: 'Granted on',
      expiresAt: 'Expires on',
      reason: 'Reason',
      endedAt: 'Closed',
      endedReason: {
        revoked: 'revoked',
        expired: 'expired (TTL)',
        reboot: 'node reboot',
        failed: 'not applied (task failed)'
      },
      grant: 'Grant read-only access…',
      grantTitle: 'Grant read-only access',
      grantSubmit: 'Grant',
      grantError: 'The grant was refused',
      grantConfirm: 'TVirt generates a temporary password for the system user «trantor» (read-only: journal and diagnostics, no sudo) and sets it on the node with a task. The password is shown ONCE, on confirmation; it ends at the deadline, at a revoke or at the node’s next reboot.',
      reasonLabel: 'Reason',
      reasonHint: 'Why you open the door: it goes on the grant, in the audit and in the journal',
      ttlLabel: 'Duration',
      ttlHours: '{n} h',
      revoke: 'Revoke read-only access…',
      revokeTitle: 'Revoke read-only access',
      revokeSubmit: 'Revoke',
      revokeError: 'The revoke was refused',
      revokeConfirm: 'The grant ends now and a task locks the user «trantor» on the node again: the temporary password stops working.',
      passwordTitle: 'Read-only password',
      passwordOnce: 'This password is shown ONCE: copy it now. It is stored nowhere and cannot be read again — if you lose it, grant again.',
      passwordUser: 'User',
      passwordLabel: 'Password',
      passwordHint: 'Valid until {until}, or until the node’s next reboot; usable once the grant task has completed. Access: ssh trantor@<node address>.',
      emergencyTitle: 'Last emergency access (trantor-emergency)',
      lastLogin: '{time} via {via} from {from}',
      lastLoginNone: 'none observed',
      lastLoginVia: {
        ssh: 'ssh',
        console: 'console',
        sudo: 'sudo',
        unknown: 'unknown'
      },
      emergencyHint: 'The emergency account «trantor-emergency» (full sudo) is always enabled with the password generated at the node’s installation and kept in the vault: TVirt does not know it, rotate it or recover it. Every login is reported here and in the event journal.'
    },

    // #166 — the node firewall: the posture card (Impostazioni globali) and
    // the node's Firewall tab (the lens over the derived registry and the
    // node component's state).
    nodeFirewall: {
      tab: 'Firewall',
      title: 'Node firewall',
      subtitle: 'The posture of the nftables ruleset every node applies to itself — the only firewall of a node (firewalld is disabled)',
      modeLabel: 'Posture',
      mode: {
        disabled: 'Disabled — no ruleset on the nodes',
        permissive: 'Permissive — counts and logs, drops nothing',
        enforced: 'Enforced — only the derived registry gets in'
      },
      hint: {
        disabled: 'As today: the nodes carry no ruleset. Flip to permissive to READ what enforced would drop before it is dropped.',
        permissive: 'Every node applies its derived ruleset at policy accept: what enforced would drop is counted and logged (journal prefix «tvirt-node would-drop:»). Nothing is blocked.',
        enforced: 'Every node applies its derived ruleset at policy drop: ssh and the API from anywhere, store / live migration / overlay / Ceph from the members only, network-service listeners from their served network. Each flip is an apply to confirm — a node that cannot confirm rolls back by itself.'
      },
      confirmTitle: 'Enforce the node firewall?',
      confirmBody: 'Every node applies the ruleset to confirm: it probes its backend, store and ssh through the new rules within the deadline and confirms — otherwise it rolls back by itself to the previous ruleset. Read the permissive counters on the nodes first.',
      confirmSubmit: 'Enforce',
      confirmCancel: 'Cancel',
      lensTitle: 'Node firewall',
      loadError: 'Could not load the node firewall lens',
      noReport: 'No firewall report from this node yet — the worker reports at its next pass',
      firewalldActive: 'firewalld is running on this node: the node firewall refuses to apply beside it (mask firewalld)',
      conditionUnconfirmed: 'No confirmed ruleset yet — the node is open',
      conditionRolledBack: 'The last apply rolled back — the same generation is not retried until the registry changes or the posture is flipped again',
      lastError: 'Last error',
      posture: 'Posture',
      observedAt: 'Observed',
      desiredGeneration: 'Desired generation',
      componentTitle: 'Node component',
      componentSubtitle: 'tvirt-node-firewall — applies the confirmed ruleset at boot, rolls an unconfirmed one back by itself',
      componentAbsent: 'The component has not reported yet',
      phase: 'Phase',
      phases: {
        none: 'Never applied',
        applied: 'Applied — awaiting confirm',
        confirmed: 'Confirmed',
        rolledBack: 'Rolled back'
      },
      generation: 'Generation',
      appliedAt: 'Applied at',
      confirmedAt: 'Confirmed at',
      deadline: 'Rollback deadline',
      lastRollback: 'Last rollback',
      registryTitle: 'Derived registry',
      registrySubtitle: 'The ports this node opens, by class and origin — derived from the cluster, never edited by hand',
      registryEmpty: 'Nothing derived — posture disabled with no confirmed ruleset, or no report yet',
      colClass: 'Class',
      colPort: 'Port',
      colSource: 'Source',
      colOrigin: 'Origin',
      classes: {
        public: 'Public',
        internode: 'Inter-node',
        tenant: 'Tenant'
      },
      sourceMembers: 'members only',
      sourceAnywhere: 'anywhere',
      sourceBridge: 'on {iface}'
    },
    standaloneHint: 'This node has no cluster: it has not been installed yet. Clusters are created OR joined at install — a join token minted from a member\u2019s Servers table is fed to the installer (dev: db:init-cluster / db:init-join).',
    reducedHint: 'Your permissions cover a scope, not the whole cluster: the fleet inventory — nodes, settings and devices — is visible only to cluster administrators.',
    phaseReady: 'Ready',
    phaseForming: 'Forming',
    storeMembers: 'Store members',
    watermark: 'Convergence floor',
    configGeneration: 'Config generation',
    converged: 'converged',
    behind: 'behind — {m} / {n}',
    revision: 'Revision',
    rolesTitle: 'Network roles',
    rolesSubtitle: 'Per-member addresses for each traffic class — connectivity itself lives in the host network',
    roleManagement: 'Management',
    roleLiveMigration: 'Live Migration',
    roleInherited: 'inherits management',
    settingsTitle: 'Settings',
    settingsSubtitle: 'Cluster-wide security policy — evaluated by every node from its local replica',
    taskRetentionTitle: 'Task retention',
    taskRetentionSubtitle: 'Unified GC for all tasks — finished tasks past the age or the cap are collected, running ones never',
    taskRetentionMaxAge: 'Maximum age (days)',
    taskRetentionMaxCount: 'Maximum stored tasks',
    eventRetentionTitle: 'Event retention',
    eventRetentionSubtitle: "The journal's GC — a policy independent from the tasks': the task is a queue, the event is memory",
    eventRetentionMaxAge: 'Maximum age (days)',
    eventRetentionMaxCount: 'Maximum stored events',
    spaceThresholdsTitle: 'Space thresholds',
    spaceThresholdsSubtitle: 'When a filling storage enters the journal (and the notifications)',
    spaceLowPct: 'Warning from (% used)',
    spaceCriticalPct: 'Critical from (% used)',
    spaceOrderError: 'Critical cannot sit below warning',
    spaceThresholdsHint: 'Only the entries are configured: the exit is derived by the engine, 5 points below each threshold, so a storage sitting on the line never becomes a metronome',
    failoverReserveTitle: 'Failover reserve',
    failoverReserveSubtitle: 'Admission control N+1 — RAM kept free so the cluster can restart its workload after losing nodes',
    failoverReserveTolerate: 'Node failures to tolerate',
    failoverReserveOff: '0 — the reserve is off: no start is ever refused for failover capacity',
    failoverReserveOn: 'A VM start that would eat the reserve is refused with a 412 explaining the numbers',
    networkShapingTitle: 'Bandwidth fairness',
    networkShapingSubtitle: 'The three typology weights of the per-uplink shaping tree — they bite only under contention',
    networkShapingRest: 'Rest weight (control plane, storage, replication)',
    networkShapingOverlay: 'Overlay networks weight',
    networkShapingLiveMigration: 'Live migration weight',
    networkShapingHint: 'Guaranteed share = weight / sum of the three (defaults 300 / 100 / 200, each from 1 to 10000); on an idle link every class may take the whole bandwidth',
    debugModeTitle: 'Debug mode',
    debugModeSubtitle:
      'Detailed errors in the generic GUI alerts — to see the real cause when a «failed» action actually succeeded',
    debugModeLabel: 'Debug mode enabled',
    debugModeOn: 'Generic error alerts append the underlying cause — internal details visible to every operator',
    debugModeOffHint: 'Off — generic error alerts keep their usual wording, with no internal details',
    nodeDetailsAction: 'Node details',
    node: {
      docTitle: 'Node',
      notFound: 'Node not found',
      tabOverview: 'Overview',
      tabStats: 'Statistics',
      tabNetwork: 'Network',
      tabSettings: 'Local settings',
      identityTitle: 'Identity',
      heartbeatTitle: 'Heartbeat',

      // Round #156 (dialog standard): the registry tail the detail was missing.
      ysqlPort: 'YSQL port',
      revision: 'Config revision',
      versionsTitle: 'Observed versions',
      heartbeatSubtitle: 'What the node last reported about itself',
      cpuTitle: 'CPU',
      cpuModelTitle: 'CPU model',
      memoryTitle: 'Memory',
      hugePagesTitle: 'Huge pages',
      hugePagesPool: '{size}: {free} free of {total}',
      hugePagesNone: 'No pool',
      hugePagesNoneHint: 'The node has no huge pages reserved: VMs that ask for them cannot start here. The pool is declared on the node, with the kernel\'s hugepagesz and hugepages parameters or by writing nr_hugepages.',
      usageTitle: 'Current usage',
      usageMem: 'RAM {n} MiB',
      usageUnknown: 'not observed — no fresh sample',
      statsNoData: 'No data for the selected range — node series are sampled by the node itself and served locally.',
      observedTitle: 'Observed state',
      observedSubtitle: 'Services and node-local assets, as seen by the worker'
    },
    editRolesTitle: 'Edit network roles',
    memberNode: 'Node',
    memberIp: 'IP address',
    addMember: 'Add member',
    lmInherit: 'Use the management addresses',
    roleIpHint: "Adding and removing members is free; changing an existing member's IP in place is refused — the IP-migration flow arrives in a later round.",
    roleError: 'Could not update the roles',
    nodesTitle: 'Nodes',
    addNodeTitle: 'Add node to cluster',
    addNode: 'Add node',
    addNodeError: 'Could not pre-register the node',
    nodeName: 'Node name',
    nodeNameHint: 'must match the name set at installation',
    nodeIp: 'Management IP',
    colName: 'Name',
    colPhase: 'Phase',
    colServices: 'Services',
    colWatermark: 'Watermark',
    colLastSeen: 'Last seen',
    colUps: 'UPS',
    upsCordonChip: 'UPS cordon',
    upsCordonTip: 'The node condemned itself on its UPS facts ({time}): it releases the cordon by itself once mains is stable and the battery recharged',
    upsUnreachable: 'UPS unreachable',
    upsOnBattery: 'On battery',
    upsOnLine: 'On mains',
    upsLowBattery: 'low battery',
    upsNoEstimateChip: 'runtime not estimated',
    upsNoEstimateTip: 'The UPS publishes no runtime estimate: the node is condemned by the UPS’s LB (low battery) flag alone — tune battery.runtime.low / LB on the UPS itself',
    upsLaneTip: 'UPS shutdown lane since {since}: condemned by {by}',
    upsUnit: 'unit',
    upsStatus: 'NUT status',
    upsLoad: 'load',
    upsOnBatterySince: 'on battery since',
    upsOnLineSince: 'on mains since',
    upsError: 'error',
    upsObservedAt: 'observed',
    upsNone: 'no UPS unit serves this node',
    upsBy: {
      runtime: 'runtime below the threshold',
      lowBattery: 'the UPS’s LB flag',
    },
    upsLane: {
      evacuate: 'evacuation',
      local: 'shutdown in place',
    },
    upsPhase: {
      draining: 'drain',
      stoppingGuests: 'stopping guests',
      stoppingServices: 'stopping services',
      poweredOff: 'powered off',
    },
    upsTitle: 'UPS (NUT)',
    upsSubtitle: 'UPS units and thresholds: the node is condemned at runtime ≤ threshold or on the LB flag (always honoured), resumes with hysteresis',
    upsRuntimeThreshold: 'Runtime threshold (s)',
    upsRuntimeThresholdHint: 'On battery, with the estimated runtime ≤ threshold the node shuts down in order (default 300)',
    upsResumeStable: 'Mains stable for (s)',
    upsResumeStableHint: 'Resume only with mains continuous for at least this many seconds (default 60)',
    upsResumeCharge: 'Minimum charge to resume (%)',
    upsResumeChargeHint: 'And the battery recharged to at least this percentage (default 25)',
    upsPolicyNote: 'The UPS’s LB (low battery) flag always condemns and cannot be switched off; there is no threshold on the time spent on battery. Every unit may override the three numbers.',
    upsUnitNew: 'new unit',
    upsUnitSelectors: '{n} tag selector(s) (CLI only)',
    upsUnitName: 'Unit name',
    upsUnitMode: 'Mode',
    upsModeExternal: 'external (somebody else’s upsd)',
    upsModeManaged: 'managed (UPS cabled to a node)',
    upsUnitUpsname: 'NUT name (upsname)',
    upsUnitHost: 'upsd host',
    upsUnitAttachedNode: 'Attached node',
    upsUnitDriver: 'NUT driver',
    upsUnitDriverHint: 'usbhid-ups, nutdrv_qx, snmp-ups, …',
    upsUnitDevice: 'Driver device',
    upsUnitDeviceHint: 'the port= line of ups.conf: empty = auto',
    upsUnitPort: 'upsd port',
    upsUnitServedNodes: 'Served nodes',
    upsUnitServedNodesHint: 'node names separated by commas (tag selectors go through the CLI)',
    upsUnitOverrideHint: 'empty = cluster default',
    upsUnitAdd: 'Add unit',
    unreachableSince: 'Unreachable — last heartbeat {time}',
    overlaysChip: 'overlays',
    overlaysBehind: 'The overlay dataplane has not yet realized the current overlay configuration',
    overlaysError: 'Last overlay reconcile pass failed: {error}',
    derivationDerived: 'derived',
    derivationDiverged: 'diverged',
    derivationDerivedTip: 'The network document is DERIVED from fabric {fabric} and still matches the render on current inputs — click for the derivation plan.',
    derivationDivergedTip: 'The derivation from fabric {fabric} changed after the last apply (fabric, cabling, roles or references moved) — click for the pending diff.',
    derivationRetiring: plural({ one: '{n} address draining', other: '{n} addresses draining' }),
    storeChip: 'store',
    phase: {
      'pending-join': 'Pending join',
      'joining': 'Joining',
      'member': 'Member'
    },
    tokenTitle: 'Join token',
    tokenOnce: 'Shown only now — copy it immediately, it can never be read again',
    copy: 'Copy',
    copied: 'Copied',
    close: 'Close',
    revokeTitle: 'Revoke pre-registration',
    revoke: 'Revoke',
    revokeError: 'Could not revoke the registration',
    promote: 'Promote to store member',
    demote: 'Remove store-member role',
    troubleshoot: 'Troubleshoot',
    promoteTitle: 'Promote to store member',
    demoteTitle: 'Remove store-member role',
    promoteHint: 'The node joins the YugabyteDB master quorum. Real HA needs three store members.',
    demoteHint: 'The node leaves the master quorum and stops its store services.',
    storeError: 'The store-member change was refused',
    attest: 'Attest powered off…',
    attestTitle: 'Attest node powered off',
    attestSubmit: 'Attest powered off',
    attestConfirm: 'You are declaring this node powered off, at your own risk: attesting a node that is actually alive risks double-running VMs. Its tasks are failed and its VM authorities become transferable.',
    attestError: 'The attestation was refused',
    attestedChip: 'attested down',
    maintenanceChip: 'maintenance',
    guestToolsChip: 'TVirt Guest Tools (node-local ISO asset)',
    maintenanceEnter: 'Enter maintenance',
    maintenanceExit: 'Exit maintenance',
    maintenanceEnterTitle: 'Enter maintenance',
    maintenanceExitTitle: 'Exit maintenance',
    maintenanceEnterSubmit: 'Enter maintenance',
    maintenanceExitSubmit: 'Exit maintenance',
    maintenanceEnterConfirm: 'The node is cordoned and drained: the network services and the App instances whose authority it holds are transferred (instances cold: stopped and reaped here, recreated on the target — downtime), its running VMs are live-migrated where possible and gracefully stopped otherwise, in-flight tasks finish, new work is held back, and the stopped VMs lose their authority — Proximity re-places them at the next start. Nothing comes back by itself when maintenance ends.',
    maintenanceExitConfirm: 'The node resumes claiming work and Proximity considers it again for placement. VMs return only when started.',
    maintenanceError: 'The maintenance request was refused',
    maintenancePlanTitle: 'Evacuation plan',
    maintenancePlanLoading: 'Computing the evacuation plan…',
    maintenancePlanError: 'The evacuation plan could not be loaded — the drain will still migrate where possible and stop the rest.',
    maintenancePlanEmpty: 'Nothing to evacuate on this node — no enabled network service, no wanted-running App instance, no live VM.',
    maintenancePlanSummary: '{migrate} object(s) will migrate (VMs, network services, App instances), {stop} will stop or stay where they are.',
    maintenancePlanReserve_ok: 'Failover reserve after the drain: intact — {headroom} GiB still startable while tolerating the loss of {n} node(s).',
    maintenancePlanReserve_eroded: 'Failover reserve after the drain: nearly consumed — only {headroom} GiB left of the {reserve} GiB kept free for {n} node failure(s).',
    maintenancePlanReserve_exhausted: 'Failover reserve after the drain: EXHAUSTED — short by {headroom} GiB of the {reserve} GiB needed to tolerate {n} node failure(s). New VM starts will be refused while this node is out.',
    maintenancePlanReserve_unknown: 'Failover reserve after the drain: not computable — no remaining node has reported its memory.',
    maintenancePlanReserveHugePages: 'Huge pages of {size} after the drain — free: {free} of {capacity}, required as reserve: {reserve}.',
    maintenancePlanActionMigrate: 'migrates',
    maintenancePlanActionMigrateWarning: 'migrates (soft rule violated)',
    maintenancePlanActionStop: 'stops',
    maintenancePlanActionBlocked: 'blocked',
    maintenancePlanKindVm: 'VM',
    maintenancePlanKindNetsvc: 'network service',
    maintenancePlanKindApp: 'App instance',
    maintenancePlanColdMove: "Cold move: the pod is stopped and reaped here and recreated on the target by its worker — downtime until the target worker's next pass.",
    caFingerprint: 'Cluster CA fingerprint',
    staleChip: 'stale',
    troubleshootTitle: 'Store troubleshooting',
    restartStore: 'Restart store services',
    restartHint: 'If the store is misbehaving, bounce its services. Advanced — use with care.',
    restartQueued: 'Restart task queued',
    restartError: 'Could not restart the services',
    svcActive: 'active',
    svcWanted: 'wanted, not up',
    svcStopped: 'stopped',
    noServices: 'No services reported'
  },
  clusterDevices: {
    gpusTitle: 'GPU / PCIe',
    gpusSubtitle: 'The cluster-wide GPU inventory grouped by model — observed cards per node, with the marked registrations and their tags',
    usbTitle: 'USB resources',
    usbSubtitle: 'The cluster-wide USB inventory grouped by model — observed devices per node, with the registrations of both models (by device, by port) and their tags',
    markedChip: '{n} usable by TVirt',
    orphanChipGpu: '{n} orphaned',
    orphanChipUsb: '{n} orphaned',
    colNode: 'Node',
    colRegistration: 'Registration',
      noRegsHint: 'No registration yet — mark the device as usable by TVirt from the node dialog (Nodes › node › Settings).',
      colDevice: 'Device',
      colObserved: 'Observed',
      colRegs: 'Registrations',
      regsNone: 'none',
      colPci: 'PCIe address',
      colIdentity: 'Identity / port',
      colClasses: 'Classes (tags)',
      colActions: 'Actions',
      manageOnNode: 'Manage on node {node} (marking lives in the node dialog)',
      introTitle: 'The cluster-wide inventory of USB and PCIe devices usable by TVirt©.',
      introStep1: '1. Nodes discover their GPU and USB devices automatically (heartbeat census).',
      introStep2: '2. You mark a device as usable by TVirt from the NODE dialog (Nodes › node › Settings) — that binds it to vfio and registers it here.',
      introStep3: '3. Here you give registrations their CLASSES via tags (e.g. gpu_class=compute): VM requirements will ask for a class, and the scheduler picks a node with a free matching device.',
    colMode: 'Mode',
    colState: 'State',
    colNotes: 'Notes',
    colTags: 'Tags',
    modeDevice: 'by device',
    modePort: 'by port',
    usable: 'usable',
    orphanGpu: 'orphaned',
    orphanUsb: 'orphaned',
    hubChip: 'hub',
    orphanPortsTitle: 'Port registrations with nothing plugged — reattach a device or unmark them from the node',
    gpusEmpty: 'No GPUs observed yet on any node',
    usbEmpty: 'No USB devices observed yet on any node'
  },

  // Shared settings-form vocabulary (settings refactor, 2026-07-24): the security
  // fields feed the Cluster page's settings card, the node fields the node-settings
  // page — the /system/settings page these were born for is gone.
  settings: {
    passwordMinLength: 'Minimum password length',
    sessionIdle: 'Session idle timeout (minutes)',
    lockoutThreshold: 'Lockout after failed attempts',
    lockoutDuration: 'Lockout duration (minutes)',
    keyboardLayout: 'Keyboard layout',
    keyboardLayoutHint: 'e.g. it, en-us — empty for the QEMU default',
    syslogEndpoint: 'Syslog endpoint',
    syslogEndpointHint: 'host:port — empty to disable forwarding',
    syslogTls: 'TLS',
    maxQueued: 'Max queued tasks',
    concurrency: 'Worker concurrency',
    concurrencyHint: 'Applied live by the worker',
    notesTitle: 'Notes',
    save: 'Save',
    saved: 'Settings saved',
    saveError: 'Could not save the settings'
  },
  localSettings: {
    nodeSettingsTitle: 'Node settings',
    nodeSettingsSubtitle: 'What this node enforces — task queue, console, log forwarding — plus its tags and notes',
    baseTitle: 'Host reservations (TVirt Proximity)',
    baseSubtitle: 'CPU and RAM set aside for the host OS — the inputs of the empirical capacity Proximity places VMs by',
    baseMemory: 'Base RAM (MiB)',
    baseMemoryHint: 'Observed total: {total} MiB. Usable for VMs = total − base − RAM of the live VMs placed here.',
    baseMemoryHintNoTotal: 'Usable for VMs = observed total − base − RAM of the live VMs placed here.',
    baseCpu: 'Base CPU cores',
    baseCpuHint: 'Observed logical CPUs: {total}. Used as a ranking weight only — vCPU overcommit is normal.',
    baseCpuHintNoTotal: 'Used as a ranking weight only — vCPU overcommit is normal.',
    baseHint: 'RAM is the hard admission gate: a VM that does not fit the usable RAM is refused with a speaking error. Raise the base on hosts running store members or Ceph daemons.',
    placementTitle: 'Physical placement',
    placementSubtitle: 'Where this server physically stands — declared once, whatever it serves',
    placementHint: 'The placement drives the CRUSH hierarchy of every Ceph cluster with an OSD on this server: saving a change realigns their maps through a task (host → rack → … → root). Partial declarations are legal — empty levels simply collapse. Names are shared across the whole map: two servers in the same rack declare the same rack name.',
    placementNameInvalid: 'DNS-safe name (lowercase letters, digits, hyphens)',
    placementEmpty: 'Empty = not declared',
    placementLevel: {
      chassis: 'Chassis',
      rack: 'Rack',
      row: 'Row',
      pdu: 'PDU',
      pod: 'Pod',
      room: 'Room',
      datacenter: 'Datacenter',
      zone: 'Zone',
      region: 'Region'
    },
    cablingTitle: 'Cabling',
    cablingSubtitle: 'Which switch port each interface plugs into — the declaration lives on the node (Annex O.9)',
    cablingHint: 'One cable per NIC, one cable per port fleet-wide (the API refuses naming the holder). Cabling toward a LAGG-member port is legal — that is the bond case: the future derivation renders it as LACP.',
    cablingInterface: 'Interface',
    cablingNotCabled: '— not cabled —',
    cablingNoInterfaces: 'No network interfaces observed yet',
    disksTitle: 'Disks',
    disksSubtitle: 'Observed block devices — one sweep per reconcile pass; mark the disks usable as Ceph OSDs here (N.15: eligibility is explicit)',
    colDataDisk: 'Data disk',
    dataDisksHint: 'Ticking a device declares it usable as an OSD; the serial is its identity pin — copied from the hardware when the device reports one, minted (tvirt-…) otherwise. The Ceph register dialog offers only declared disks.',
    diskDeclaredSerial: 'declared',
    diskNotObserved: 'declared but not observed — re-pin the device or unmark it',
    noDisks: 'No block devices observed yet',
    colSerial: 'Serial',
    colModel: 'Model',
    colSize: 'Size (GiB)',
    colDiskType: 'Type',
    colInUse: 'In use',
    colObservedAt: 'Observed',
    diskInUse: 'in use',
    diskFree: 'free',
    diskMounted: 'mounted',
    diskPartitions: 'partitions',
    passthroughTitle: 'Passthrough devices',
    passthroughSubtitle: 'Observed GPUs and USB devices — mark here what TVirt may pass to VMs; marking registers the device identity (round U1+G1)',
    passthroughHint: 'Marking registers the device (a stable id — the target of tags and class claims). A marked GPU has its WHOLE IOMMU group bound to vfio-pci at once — it leaves the host immediately; unmarking rebinds the native driver. USB offers both ratified models: by device (vendor+product, wherever it is plugged) and by physical port (the VM gets whatever is plugged there). USB never binds at marking — the attach happens at VM runtime.',
    passthroughGpusTitle: 'GPU / PCIe',
    passthroughUsbTitle: 'USB',
    colUsable: 'Usable by TVirt',
    colGpu: 'GPU',
    colPciAddress: 'PCI address',
    colDriver: 'Driver',
    colVerdict: 'Verdict',
    gpuEligible: 'Eligible',
    gpuIneligible: 'Not eligible',
    gpuReason: {
      'no-iommu': 'IOMMU not active on the host',
      'group-spans-slots': 'IOMMU group not isolated: it spans other slots',
      'multiple-display': 'IOMMU group not isolated: more than one display function',
      'foreign-function': 'IOMMU group not isolated: it carries a foreign function'
    },
    vfioNotReady: 'IOMMU is not active on this host: no GPU is eligible until the host platform enables it (OEM ISO) — TVirt never fixes up kernel args at runtime.',
    vfioMissingKernelArg: 'IOMMU is not active on this host: the firmware reports VT-d/AMD-Vi (ACPI DMAR/IVRS table present) but the kernel arg is missing — add intel_iommu=on (Intel) or remove amd_iommu=off (AMD) from the kernel command line and reboot. TVirt never fixes up kernel args at runtime.',
    vfioFirmwareDisabled: 'IOMMU is not active on this host: VT-d/AMD-Vi is disabled in the firmware (no ACPI DMAR/IVRS table) or the CPU has no support — enable it in the firmware setup, no kernel arg can turn it on. TVirt never fixes up the host at runtime.',
    gpuBindError: 'vfio-pci bind failed on {address}: {error}',

    // Firewall su bridge non gestiti (#168 §9-bis): la lista del consenso.
    firewallBridgesTitle: 'Firewall on unmanaged bridges',
    firewallBridgesSubtitle: 'Hand-built bridges on this node whose setup consents to TVirt-rendered firewall chains on attached guest NICs',
    firewallBridgesHint: 'TVirt never touches the foreign bridge — the chain anchors on the tap/veth, which is always TVirt\'s own. The list is a consent gate: default-deny and anti-spoof can break what a manual setup expects, so a firewall-bearing NIC on an unmanaged bridge NOT listed here is refused with a speaking error.',
    firewallBridgesEmpty: 'No unmanaged bridge consents to TVirt-rendered firewall chains on this node.',
    firewallBridgeAdd: 'Add bridge',
    firewallBridgeInvalid: 'Not a Linux interface name (1-15 characters, no slash or space)',
    firewallBridgeDuplicate: 'Name already listed',
    colMarkDevice: 'By device',
    colMarkPort: 'By port',
    usbHub: 'hub',
    colUsbId: 'Vendor:Product',
    colPort: 'Port',
    passthroughOrphan: 'orphaned — registered but no longer detected: reattach it or unmark it',
    noGpus: 'No GPUs observed yet',
    noUsb: 'No USB devices observed yet',
    networkGeneration: 'Network generation',
    applied: 'Applied',
    converged: 'converged',
    behind: 'behind — {m} / {n}',
    notConfigured: 'not configured',
    rollbackBody: 'Apply of generation {gen} failed: {error}',
    rolledBackChip: 'rolled back',
    nicsTitle: 'NICs',
    nicsSubtitle:
      'The managed-NIC registry — registration IS the management declaration: a registered NIC with no configuration is left untouched (clear-if declares it explicitly clean), an unregistered device is never touched',
    addNic: 'Register NIC',
    addNicTitle: 'Register NIC (add-nic)',
    delNic: 'End management',
    delNicTitle: 'End management (del-nic)',
    delNicConfirm:
      'Management of {name} ends: the registry entry and its configuration are dropped and the TVirt profile removed — the device itself is left as Linux has it.',
    nicName: 'Device name',
    nicMac: 'Permanent MAC address',
    nicMacHint: 'the permanent hardware MAC — the stable identity set by the TVirt Server Installer',
    colObserved: 'Observed',
    nicObserved: 'observed',
    nicNotObserved: 'not observed',
    nicMacMismatch: 'MAC mismatch — observed {mac}',
    noNics: 'No NICs registered — register a device to start managing it',
    noManagedDevices: 'No managed devices — register a NIC first, everything builds on the registry',
    inventoryTitle: 'Observed devices',
    inventorySubtitle: 'Refreshed each reconcile pass by the node worker — validation checks device names against this inventory',
    colDevice: 'Device',
    colType: 'Type',
    colMac: 'MAC',
    colLink: 'Link',
    colSpeed: 'Speed',
    colTopology: 'Topology',
    colMtu: 'MTU',
    colAddresses: 'Addresses',
    linkUp: 'up',
    linkDown: 'down',
    lacpDegraded: 'LACP not aggregating',
    noInventory: 'No devices reported yet',
    interfacesTitle: 'Interfaces',
    interfacesSubtitle:
      'L3 configuration for registered NICs and declared logical devices — set-if replaces the whole block, edit-if patches properties, clear-if empties it (explicit clean)',
    colConfig: 'Desired configuration',
    clean: 'clean',
    setIf: 'Edit addressing',
    setIfTitle: 'Edit addressing (set-if)',
    editIf: 'Edit properties',
    editIfTitle: 'Edit properties (edit-if)',
    clearIf: 'Clear configuration',
    clearIfTitle: 'Clear configuration (clear-if)',
    clearIfConfirm: 'The interface configuration is emptied. A clean interface is the prerequisite for enslaving it as a bond/bridge member or VLAN parent.',
    ip4Mode: 'IPv4 mode',
    ip6Mode: 'IPv6 mode',
    modeUnconfigured: 'Unconfigured',
    modeStatic: 'Static',
    modeDhcp: 'DHCP',
    ip4Addresses: 'IPv4 addresses',
    ip6Addresses: 'IPv6 addresses',
    addressesHint: 'CIDR, comma-separated',
    gw4: 'IPv4 gateway',
    gw6: 'IPv6 gateway',
    dns: 'DNS servers',
    dnsHint: 'comma-separated — empty clears DNS (set-if replaces the block)',
    routes4: 'IPv4 static routes',
    routes6: 'IPv6 static routes',
    routeTo: 'Destination (CIDR)',
    routeVia: 'Via',
    addRoute: 'Add route',
    routesChip: 'routes',
    mtu: 'MTU',
    mtuHint: 'empty = driver default',
    vlansTitle: 'VLANs',
    addVlan: 'Add VLAN',
    addVlanTitle: 'Add VLAN (add-vlan)',
    delVlanTitle: 'Delete VLAN (del-vlan)',
    vlanParent: 'Parent interface',
    vlanId: 'VLAN ID',
    vlanPreview: 'Device name',
    colParent: 'Parent',
    colVlanId: 'ID',
    noVlans: 'No VLANs declared',
    bondsTitle: 'Bonds',
    addBond: 'Add bond',
    addBondTitle: 'Add bond (add-bond)',
    editBondTitle: 'Edit bond',
    editBond: 'Save bond',
    delBondTitle: 'Delete bond (del-bond)',
    bondName: 'Bond name',
    members: 'Members',
    membersHint: 'members must be clean interfaces (no L3 configuration)',
    bondMode: 'Mode',
    modeActiveBackup: 'active-backup',
    modeLacp: 'LACP (802.3ad)',
    miimon: 'miimon (ms)',
    primary: 'Primary member',
    rate: 'LACP rate',
    hash: 'Transmit hash policy',
    colMembers: 'Members',
    colMode: 'Mode',
    noBonds: 'No bonds declared',
    bridgesTitle: 'Bridges',
    addBridge: 'Add bridge',
    addBridgeTitle: 'Add bridge (add-bridge)',
    editBridgeTitle: 'Edit bridge',
    editBridge: 'Save bridge',
    delBridgeTitle: 'Delete bridge (del-bridge)',
    bridgeName: 'Bridge name',
    stp: 'STP',
    stpOn: 'STP on',
    stpOff: 'STP off',
    colStp: 'STP',
    noBridges: 'No bridges declared',
    hostnameTitle: 'Hostname',
    hostname: 'Hostname',
    setHostname: 'Set hostname',
    setHostnameTitle: 'Set hostname (set-hostname)',
    hostnameHint: 'OS identity only — the cluster node name is not touched',
    notSet: 'not set',
    staticHostsTitle: 'Static host entries',
    addStaticHost: 'Add entry',
    addStaticHostTitle: 'Add static host entry',
    delStaticHostTitle: 'Delete static host entry',
    hostIp: 'IP address',
    hostNames: 'Names',
    hostNamesHint: 'comma or space separated',
    colIp: 'IP',
    colNames: 'Names',
    noStaticHosts: 'No static host entries',
    delConfirm: 'Removing {name} from the desired document — the node worker converges through a NetworkManager checkpoint with automatic rollback.',
    remove: 'Remove',
    derivedChip: 'derived',
    derivedTip: 'This device is OWNED by the fabric derivation (H2): the editor refuses to touch it — edit the fabric (or the cabling) and re-apply, or take it back below.',
    takeOwnership: 'Take ownership',
    takeOwnershipTitle: 'Take ownership (take-ownership)',
    takeOwnershipConfirm: '{name} leaves the fabric block and becomes a MANUAL device this editor governs again. The node turns diverged, and the next derivation apply will refuse on it (manualConflict) until you rename or remove it — or fix the fabric so it is no longer derived.',
    opError: 'The network operation was refused'
  }
}

export const itIT: typeof enUS = {
  cluster: {
    // #233 — gli accessi di garanzia di un nodo (R4, T233-P3-bis): la concessione
    // read-only (password generata dal backend, mostrata UNA volta), il suo
    // record sul nodo e l'ultimo login dell'account emergency dal journal.
    nodeAccess: {
      title: 'Accessi di garanzia',
      subtitle: 'La porta di servizio del nodo: l’accesso read-only a password temporanea e l’account di emergenza con la password della cassaforte',
      readOnlyTitle: 'Accesso read-only (trantor)',
      stateNone: 'chiuso',
      stateGranted: 'aperto',
      stateGrantedPending: 'concesso — in applicazione sul nodo',
      grantedBy: 'Concesso da',
      grantedAt: 'Concesso il',
      expiresAt: 'Scade il',
      reason: 'Causale',
      endedAt: 'Chiuso',
      endedReason: {
        revoked: 'revocato',
        expired: 'scaduto (TTL)',
        reboot: 'riavvio del nodo',
        failed: 'non applicato (task fallito)'
      },
      grant: 'Concedi accesso read-only…',
      grantTitle: 'Concedi accesso read-only',
      grantSubmit: 'Concedi',
      grantError: 'La concessione è stata rifiutata',
      grantConfirm: 'TVirt genera una password temporanea per l’utente di sistema «trantor» (sola lettura: journal e diagnostica, nessun sudo) e la imposta sul nodo con un task. La password viene mostrata UNA sola volta, alla conferma; cessa alla scadenza, alla revoca o al prossimo riavvio del nodo.',
      reasonLabel: 'Causale',
      reasonHint: 'Perché apri la porta: finisce nella concessione, nell’audit e nel giornale',
      ttlLabel: 'Durata',
      ttlHours: '{n} h',
      revoke: 'Revoca accesso read-only…',
      revokeTitle: 'Revoca accesso read-only',
      revokeSubmit: 'Revoca',
      revokeError: 'La revoca è stata rifiutata',
      revokeConfirm: 'La concessione termina subito e un task blocca di nuovo l’utente «trantor» sul nodo: la password temporanea smette di valere.',
      passwordTitle: 'Password read-only',
      passwordOnce: 'Questa password viene mostrata UNA sola volta: copiala adesso. Non è salvata da nessuna parte e non potrà essere riletta — se la perdi, concedi di nuovo.',
      passwordUser: 'Utente',
      passwordLabel: 'Password',
      passwordHint: 'Vale fino al {until}, o fino al prossimo riavvio del nodo; diventa utilizzabile quando il task di concessione è completato. Accesso: ssh trantor@<indirizzo del nodo>.',
      emergencyTitle: 'Ultimo accesso di emergenza (trantor-emergency)',
      lastLogin: '{time} via {via} da {from}',
      lastLoginNone: 'nessuno osservato',
      lastLoginVia: {
        ssh: 'ssh',
        console: 'console',
        sudo: 'sudo',
        unknown: 'sconosciuto'
      },
      emergencyHint: 'L’account di emergenza «trantor-emergency» (sudo pieno) è sempre abilitato con la password generata all’installazione del nodo e custodita in cassaforte: TVirt non la conosce, non la ruota e non la recupera. Ogni suo login viene riportato qui e nel giornale eventi.'
    },

    nodeFirewall: {
      tab: 'Firewall',
      title: 'Firewall dei nodi',
      subtitle: 'La postura del ruleset nftables che ogni nodo applica a se stesso — l’unico firewall di un nodo (firewalld è disabilitato)',
      modeLabel: 'Postura',
      mode: {
        disabled: 'Disabilitato — nessun ruleset sui nodi',
        permissive: 'Permissivo — conta e registra, non scarta nulla',
        enforced: 'Applicato — entra solo il registro derivato'
      },
      hint: {
        disabled: 'Com’è oggi: i nodi non portano alcun ruleset. Passa a permissivo per LEGGERE cosa verrebbe scartato prima che venga scartato.',
        permissive: 'Ogni nodo applica il proprio ruleset derivato a policy accept: ciò che il modo applicato scarterebbe viene contato e registrato (prefisso di journal «tvirt-node would-drop:»). Nulla viene bloccato.',
        enforced: 'Ogni nodo applica il proprio ruleset derivato a policy drop: ssh e API da ovunque, store / live migration / overlay / Ceph solo dai membri, i listener dei servizi di rete dalla rete servita. Ogni cambio è un apply a conferma — un nodo che non riesce a confermare torna indietro da solo.'
      },
      confirmTitle: 'Applicare il firewall dei nodi?',
      confirmBody: 'Ogni nodo applica il ruleset a conferma: sonda backend, store e ssh attraverso le nuove regole entro la scadenza e conferma — altrimenti torna da solo al ruleset precedente. Leggi prima i contatori del modo permissivo sui nodi.',
      confirmSubmit: 'Applica',
      confirmCancel: 'Annulla',
      lensTitle: 'Firewall del nodo',
      loadError: 'Impossibile caricare la lente del firewall del nodo',
      noReport: 'Nessun rapporto firewall da questo nodo, ancora — il worker riferisce al prossimo passaggio',
      firewalldActive: 'Su questo nodo gira firewalld: il firewall di nodo rifiuta di applicarsi accanto a lui (maschera firewalld)',
      conditionUnconfirmed: 'Nessun ruleset confermato, ancora — il nodo è aperto',
      conditionRolledBack: 'L’ultimo apply è tornato indietro — la stessa generazione non viene ritentata finché il registro non cambia o la postura non viene cambiata di nuovo',
      lastError: 'Ultimo errore',
      posture: 'Postura',
      observedAt: 'Osservato',
      desiredGeneration: 'Generazione desiderata',
      componentTitle: 'Componente di nodo',
      componentSubtitle: 'tvirt-node-firewall — applica al boot il ruleset confermato, riporta indietro da solo uno non confermato',
      componentAbsent: 'Il componente non ha ancora riferito',
      phase: 'Fase',
      phases: {
        none: 'Mai applicato',
        applied: 'Applicato — in attesa di conferma',
        confirmed: 'Confermato',
        rolledBack: 'Tornato indietro'
      },
      generation: 'Generazione',
      appliedAt: 'Applicato il',
      confirmedAt: 'Confermato il',
      deadline: 'Scadenza del rollback',
      lastRollback: 'Ultimo rollback',
      registryTitle: 'Registro derivato',
      registrySubtitle: 'Le porte che questo nodo apre, per classe e origine — derivate dal cluster, mai modificate a mano',
      registryEmpty: 'Nulla di derivato — postura disabilitata senza ruleset confermato, oppure nessun rapporto ancora',
      colClass: 'Classe',
      colPort: 'Porta',
      colSource: 'Sorgente',
      colOrigin: 'Origine',
      classes: {
        public: 'Pubblica',
        internode: 'Inter-nodo',
        tenant: 'Tenant'
      },
      sourceMembers: 'solo i membri',
      sourceAnywhere: 'ovunque',
      sourceBridge: 'su {iface}'
    },
    standaloneHint: "Questo nodo non ha un cluster: non è ancora stato installato. I cluster si creano O si joinano all'installazione — il token generato dalla tabella Server di un membro va dato in pasto all'installer (dev: db:init-cluster / db:init-join).",
    reducedHint: 'I tuoi permessi coprono uno scope, non l’intero cluster: l’inventario della flotta — nodi, impostazioni e dispositivi — è visibile solo a chi amministra il cluster.',
    phaseReady: 'Pronto',
    phaseForming: 'In formazione',
    storeMembers: 'Membri dello store',
    watermark: 'Soglia di convergenza',
    configGeneration: 'Generazione di config',
    converged: 'allineato',
    behind: 'indietro — {m} / {n}',
    revision: 'Revisione',
    rolesTitle: 'Ruoli di rete',
    rolesSubtitle: 'Indirizzi per membro di ogni classe di traffico — la connettività vive nella rete host',
    roleManagement: 'Management',
    roleLiveMigration: 'Live Migration',
    roleInherited: 'eredita management',
    settingsTitle: 'Impostazioni',
    settingsSubtitle: 'Policy di sicurezza a livello cluster — valutata da ogni nodo dalla propria replica locale',
    taskRetentionTitle: 'Ritenzione task',
    taskRetentionSubtitle: 'GC unificato per tutti i task — i task conclusi oltre l\'età o il tetto vengono raccolti, mai quelli in corso',
    taskRetentionMaxAge: 'Età massima (giorni)',
    taskRetentionMaxCount: 'Numero massimo di task',
    eventRetentionTitle: 'Ritenzione eventi',
    eventRetentionSubtitle: "Il GC del giornale — politica indipendente da quella dei task: il task e' una coda, l'evento e' memoria",
    eventRetentionMaxAge: "Eta' massima (giorni)",
    eventRetentionMaxCount: 'Numero massimo di eventi',
    spaceThresholdsTitle: 'Soglie di spazio',
    spaceThresholdsSubtitle: 'Quando uno storage pieno entra nel giornale (e nelle notifiche)',
    spaceLowPct: 'Avviso da (% usata)',
    spaceCriticalPct: 'Critico da (% usata)',
    spaceOrderError: "Il critico non puo' stare sotto l'avviso",
    spaceThresholdsHint: "Si configurano solo gli ingressi: il rientro lo deriva il motore, 5 punti sotto ciascuna soglia, cosi' uno storage fermo sulla riga non diventa un metronomo",
    failoverReserveTitle: 'Riserva di failover',
    failoverReserveSubtitle: 'Admission control N+1 — RAM tenuta libera perché il cluster possa riavviare il proprio carico dopo la perdita di nodi',
    failoverReserveTolerate: 'Nodi di cui tollerare la perdita',
    failoverReserveOff: '0 — riserva spenta: nessun avvio viene mai rifiutato per capacità di failover',
    failoverReserveOn: 'Un avvio che intaccherebbe la riserva viene rifiutato con un 412 che spiega i numeri',
    networkShapingTitle: 'Fairness di banda',
    networkShapingSubtitle: 'I tre pesi di tipologia dell\'albero di shaping per uplink — mordono solo sotto contesa',
    networkShapingRest: 'Peso del resto (piano di controllo, storage, repliche)',
    networkShapingOverlay: 'Peso delle reti overlay',
    networkShapingLiveMigration: 'Peso della live migration',
    networkShapingHint: 'Quota garantita = peso / somma dei tre (default 300 / 100 / 200, ognuno da 1 a 10000); a link scarico ogni classe può prendersi tutta la banda',
    debugModeTitle: 'Modalità debug',
    debugModeSubtitle:
      'Errori dettagliati negli avvisi generici della GUI — per vedere la causa vera quando un’azione «non riuscita» in realtà è riuscita',
    debugModeLabel: 'Modalità debug abilitata',
    debugModeOn: 'Gli avvisi di errore generici accodano la causa sottostante — dettagli interni visibili a ogni operatore',
    debugModeOffHint: 'Spenta — gli avvisi di errore generici restano generici, senza dettagli interni',
    nodeDetailsAction: 'Dettagli nodo',
    node: {
      docTitle: 'Nodo',
      notFound: 'Nodo non trovato',
      tabOverview: 'Panoramica',
      tabStats: 'Statistiche',
      tabNetwork: 'Rete',
      tabSettings: 'Impostazioni locali',
      identityTitle: 'Identità',
      heartbeatTitle: 'Heartbeat',
      ysqlPort: 'Porta YSQL',
      revision: 'Revisione config',
      versionsTitle: 'Versioni osservate',
      heartbeatSubtitle: "L'ultimo stato che il nodo ha riportato su di sé",
      cpuTitle: 'CPU',
      cpuModelTitle: 'Modello CPU',
      memoryTitle: 'Memoria',
      hugePagesTitle: 'Pagine enormi',
      hugePagesPool: '{size}: {free} libere su {total}',
      hugePagesNone: 'Nessun pool',
      hugePagesNoneHint: 'Il nodo non ha pagine enormi riservate: le VM che ne chiedono non possono partire qui. Il pool si dichiara sul nodo, coi parametri hugepagesz e hugepages del kernel oppure scrivendo nr_hugepages.',
      usageTitle: 'Uso corrente',
      usageMem: 'RAM {n} MiB',
      usageUnknown: 'non osservato — nessun campione fresco',
      statsNoData: "Nessun dato nell'intervallo — le serie del nodo sono campionate dal nodo stesso e servite in locale.",
      observedTitle: 'Stato osservato',
      observedSubtitle: 'Servizi e asset locali al nodo, come li vede il worker'
    },
    editRolesTitle: 'Modifica i ruoli di rete',
    memberNode: 'Nodo',
    memberIp: 'Indirizzo IP',
    addMember: 'Aggiungi membro',
    lmInherit: 'Usa gli indirizzi di management',
    roleIpHint: "Aggiungere e rimuovere membri è libero; cambiare sul posto l'IP di un membro esistente viene rifiutato — il flusso di migrazione IP arriva in un round successivo.",
    roleError: 'Impossibile aggiornare i ruoli',
    nodesTitle: 'Nodi',
    addNodeTitle: 'Aggiungi nodo al cluster',
    addNode: 'Aggiungi nodo',
    addNodeError: 'Impossibile pre-registrare il nodo',
    nodeName: 'Nome del nodo',
    nodeNameHint: "deve combaciare col nome scelto all'installazione",
    nodeIp: 'IP di management',
    colName: 'Nome',
    colPhase: 'Fase',
    colServices: 'Servizi',
    colWatermark: 'Watermark',
    colLastSeen: 'Ultimo contatto',
    colUps: 'UPS',
    upsCordonChip: 'cordone UPS',
    upsCordonTip: 'Il nodo si è condannato sui fatti del suo UPS ({time}): lo rilascia da solo quando la rete è tornata stabile e la batteria è ricaricata',
    upsUnreachable: 'UPS irraggiungibile',
    upsOnBattery: 'A batteria',
    upsOnLine: 'Su rete',
    upsLowBattery: 'batteria scarica',
    upsNoEstimateChip: 'autonomia non stimata',
    upsNoEstimateTip: 'L’UPS non pubblica la stima di autonomia: la condanna del nodo scatta dal solo flag LB (batteria scarica) dell’UPS — tara battery.runtime.low / LB sull’UPS stesso',
    upsLaneTip: 'Corsia di spegnimento UPS dal {since}: condannato da {by}',
    upsUnit: 'unità',
    upsStatus: 'stato NUT',
    upsLoad: 'carico',
    upsOnBatterySince: 'a batteria dal',
    upsOnLineSince: 'su rete dal',
    upsError: 'errore',
    upsObservedAt: 'osservato',
    upsNone: 'nessuna unità UPS serve questo nodo',
    upsBy: {
      runtime: 'autonomia sotto soglia',
      lowBattery: 'flag LB dell’UPS',
    },
    upsLane: {
      evacuate: 'evacuazione',
      local: 'spegnimento sul posto',
    },
    upsPhase: {
      draining: 'drain',
      stoppingGuests: 'spegnimento guest',
      stoppingServices: 'spegnimento servizi',
      poweredOff: 'spento',
    },
    upsTitle: 'UPS (NUT)',
    upsSubtitle: 'Unità UPS e soglie: condanna del nodo ad autonomia ≤ soglia oppure flag LB (sempre onorato), rientro con isteresi',
    upsRuntimeThreshold: 'Soglia di autonomia (s)',
    upsRuntimeThresholdHint: 'Su batteria, con autonomia stimata ≤ soglia il nodo si spegne in ordine (default 300)',
    upsResumeStable: 'Rete stabile per (s)',
    upsResumeStableHint: 'Rientro solo con rete continua da almeno tanti secondi (default 60)',
    upsResumeCharge: 'Carica minima al rientro (%)',
    upsResumeChargeHint: 'E batteria ricaricata almeno a questa percentuale (default 25)',
    upsPolicyNote: 'Il flag LB (batteria scarica) dell’UPS condanna sempre e non si disattiva; non esiste una soglia sul tempo trascorso a batteria. Ogni unità può sovrascrivere i tre numeri.',
    upsUnitNew: 'nuova unità',
    upsUnitSelectors: '{n} selettore/i per tag (solo CLI)',
    upsUnitName: 'Nome unità',
    upsUnitMode: 'Modalità',
    upsModeExternal: 'esterna (upsd altrui)',
    upsModeManaged: 'gestita (UPS collegato a un nodo)',
    upsUnitUpsname: 'Nome NUT (upsname)',
    upsUnitHost: 'Host upsd',
    upsUnitAttachedNode: 'Nodo collegato',
    upsUnitDriver: 'Driver NUT',
    upsUnitDriverHint: 'usbhid-ups, nutdrv_qx, snmp-ups, …',
    upsUnitDevice: 'Dispositivo del driver',
    upsUnitDeviceHint: 'la riga port= di ups.conf: vuoto = auto',
    upsUnitPort: 'Porta upsd',
    upsUnitServedNodes: 'Nodi serviti',
    upsUnitServedNodesHint: 'nomi dei nodi separati da virgola (i selettori per tag passano dalla CLI)',
    upsUnitOverrideHint: 'vuoto = default del cluster',
    upsUnitAdd: 'Aggiungi unità',
    unreachableSince: 'Irraggiungibile — ultimo heartbeat {time}',
    overlaysChip: 'overlay',
    overlaysBehind: 'Il dataplane overlay non ha ancora realizzato la configurazione overlay corrente',
    overlaysError: 'Ultimo pass di riconciliazione overlay fallito: {error}',
    derivationDerived: 'derivata',
    derivationDiverged: 'divergente',
    derivationDerivedTip: 'Il documento di rete è DERIVATO dalla fabric {fabric} e combacia ancora col render sugli ingressi correnti — click per il piano di derivazione.',
    derivationDivergedTip: "La derivazione dalla fabric {fabric} è cambiata dopo l'ultimo apply (fabric, cablaggio, ruoli o referenze si sono mossi) — click per il diff pendente.",
    derivationRetiring: plural({ one: '{n} indirizzo in dismissione', many: '{n} di indirizzi in dismissione', other: '{n} indirizzi in dismissione' }),
    storeChip: 'store',
    phase: {
      'pending-join': 'In attesa di join',
      'joining': 'In adesione',
      'member': 'Membro'
    },
    tokenTitle: 'Join token',
    tokenOnce: 'Mostrato solo ora — copialo subito, non sarà più leggibile',
    copy: 'Copia',
    copied: 'Copiato',
    close: 'Chiudi',
    revokeTitle: 'Revoca pre-registrazione',
    revoke: 'Revoca',
    revokeError: 'Impossibile revocare la registrazione',
    promote: 'Promuovi a store member',
    demote: 'Rimuovi il ruolo store-member',
    troubleshoot: 'Risoluzione problemi',
    promoteTitle: 'Promuovi a store member',
    demoteTitle: 'Rimuovi il ruolo store-member',
    promoteHint: 'Il nodo entra nel quorum dei master YugabyteDB. La vera HA richiede tre store member.',
    demoteHint: 'Il nodo esce dal quorum dei master e ferma i suoi servizi store.',
    storeError: 'La modifica dello store-member è stata rifiutata',
    attest: 'Attesta spento…',
    attestTitle: 'Attesta nodo spento',
    attestSubmit: 'Attesta spento',
    attestConfirm: 'Stai dichiarando questo nodo spento, a tuo rischio: attestare un nodo in realtà vivo rischia VM in doppia esecuzione. I suoi task vengono falliti e le authority delle sue VM diventano trasferibili.',
    attestError: "L'attestazione è stata rifiutata",
    attestedChip: 'attestato spento',
    maintenanceChip: 'manutenzione',
    guestToolsChip: 'TVirt Guest Tools (asset ISO locale al nodo)',
    maintenanceEnter: 'Attiva manutenzione',
    maintenanceExit: 'Disattiva manutenzione',
    maintenanceEnterTitle: 'Attiva manutenzione',
    maintenanceExitTitle: 'Disattiva manutenzione',
    maintenanceEnterSubmit: 'Attiva manutenzione',
    maintenanceExitSubmit: 'Disattiva manutenzione',
    maintenanceEnterConfirm: "Il nodo viene messo in cordone e svuotato: i servizi di rete e le istanze App di cui detiene l'autorità vengono trasferiti (le istanze a freddo: fermate e mietute qui, ricreate sul nodo di destinazione — downtime), le VM accese vengono migrate live dove possibile e spente in modo graceful altrimenti, i task in corso finiscono, il nuovo lavoro resta in attesa e le VM spente perdono la loro authority — Proximity le ripiazza al prossimo avvio. Niente torna da solo a fine manutenzione.",
    maintenanceExitConfirm: 'Il nodo riprende a lavorare e Proximity torna a considerarlo per il piazzamento. Le VM tornano solo quando vengono avviate.',
    maintenanceError: 'La richiesta di manutenzione è stata rifiutata',
    maintenancePlanTitle: 'Piano di evacuazione',
    maintenancePlanLoading: 'Calcolo del piano di evacuazione…',
    maintenancePlanError: 'Impossibile caricare il piano di evacuazione — il drain migrerà comunque dove possibile e fermerà il resto.',
    maintenancePlanEmpty: 'Niente da evacuare su questo nodo — nessun servizio di rete attivo, nessuna istanza App voluta in esecuzione, nessuna VM accesa.',
    maintenancePlanSummary: '{migrate} oggetti migreranno (VM, servizi di rete, istanze App), {stop} si fermeranno o resteranno dove sono.',
    maintenancePlanReserve_ok: 'Riserva di failover dopo lo svuotamento: integra — restano {headroom} GiB avviabili tollerando la perdita di {n} nodo/i.',
    maintenancePlanReserve_eroded: 'Riserva di failover dopo lo svuotamento: quasi consumata — restano solo {headroom} GiB dei {reserve} GiB tenuti liberi per {n} guasto/i di nodo.',
    maintenancePlanReserve_exhausted: 'Riserva di failover dopo lo svuotamento: ESAURITA — mancano {headroom} GiB dei {reserve} GiB necessari a tollerare {n} guasto/i di nodo. Finché questo nodo è fuori, i nuovi avvii di VM saranno rifiutati.',
    maintenancePlanReserve_unknown: 'Riserva di failover dopo lo svuotamento: non calcolabile — nessun nodo rimasto ha dichiarato la memoria.',
    maintenancePlanReserveHugePages: 'Pagine enormi da {size} dopo lo svuotamento — libere: {free} su {capacity}, richieste come riserva: {reserve}.',
    maintenancePlanActionMigrate: 'migra',
    maintenancePlanActionMigrateWarning: 'migra (regola soft violata)',
    maintenancePlanActionStop: 'si ferma',
    maintenancePlanActionBlocked: 'bloccata',
    maintenancePlanKindVm: 'VM',
    maintenancePlanKindNetsvc: 'servizio di rete',
    maintenancePlanKindApp: 'istanza App',
    maintenancePlanColdMove: 'Spostamento a freddo: il pod viene fermato e mietuto qui e ricreato sul nodo di destinazione dal suo worker — downtime fino al prossimo pass del worker di destinazione.',
    caFingerprint: 'Fingerprint della CA del cluster',
    staleChip: 'non aggiornato',
    troubleshootTitle: 'Risoluzione problemi dello store',
    restartStore: 'Riavvia i servizi store',
    restartHint: 'Se lo store fa i capricci, riavvia i suoi servizi. Avanzato — usare con cautela.',
    restartQueued: 'Task di riavvio in coda',
    restartError: 'Impossibile riavviare i servizi',
    svcActive: 'attivo',
    svcWanted: 'voluto, non attivo',
    svcStopped: 'fermo',
    noServices: 'Nessun servizio riportato'
  },
  clusterDevices: {
    gpusTitle: 'GPU / PCIe',
    gpusSubtitle: "L'inventario GPU dell'intero cluster raggruppato per modello — le schede osservate per nodo, con le registrazioni marcate e i loro tag",
    usbTitle: 'Risorse USB',
    usbSubtitle: "L'inventario USB dell'intero cluster raggruppato per modello — i dispositivi osservati per nodo, con le registrazioni di entrambi i modelli (per dispositivo, per porta) e i loro tag",
    markedChip: '{n} usabili da TVirt',
    orphanChipGpu: '{n} orfane',
    orphanChipUsb: '{n} orfani',
    colNode: 'Nodo',
    colRegistration: 'Registrazione',
      noRegsHint: 'Nessuna registrazione — il dispositivo si marca "usabile da TVirt" dal dialog del nodo (Nodi › nodo › Impostazioni).',
      colDevice: 'Dispositivo',
      colObserved: 'Osservati',
      colRegs: 'Registrazioni',
      regsNone: 'nessuna',
      colPci: 'Indirizzo PCIe',
      colIdentity: 'Identità / porta',
      colClasses: 'Classi (tag)',
      colActions: 'Azioni',
      manageOnNode: 'Gestisci sul nodo {node} (la marcatura vive nel dialog del nodo)',
      introTitle: "L'inventario cluster dei dispositivi USB e PCIe usabili da TVirt©.",
      introStep1: '1. I nodi scoprono da soli le proprie GPU e i dispositivi USB (censimento nel heartbeat).',
      introStep2: '2. Un dispositivo si marca "usabile da TVirt" dal dialog del NODO (Nodi › nodo › Impostazioni) — la marcatura lo lega a vfio e lo registra qui.',
      introStep3: '3. Qui dai alle registrazioni le loro CLASSI via tag (es. gpu_class=compute): i requisiti delle VM chiederanno una classe, e lo scheduler sceglierà un nodo con un dispositivo libero compatibile.',
    colMode: 'Modalità',
    colState: 'Stato',
    colNotes: 'Note',
    colTags: 'Tag',
    modeDevice: 'per dispositivo',
    modePort: 'per porta',
    usable: 'usabile',
    orphanGpu: 'orfana',
    orphanUsb: 'orfano',
    hubChip: 'hub',
    orphanPortsTitle: 'Registrazioni per porta senza dispositivo collegato — ricollega un dispositivo o smarcale dal nodo',
    gpusEmpty: 'Nessuna GPU ancora osservata su alcun nodo',
    usbEmpty: 'Nessun dispositivo USB ancora osservato su alcun nodo'
  },

  // Vocabolario condiviso dei form impostazioni (refactoring 2026-07-24): i campi
  // security alimentano la card della pagina Cluster, i campi nodo la pagina
  // node-settings — la pagina /system/settings per cui nacquero è morta.
  settings: {
    passwordMinLength: 'Lunghezza minima password',
    sessionIdle: 'Timeout inattività sessione (minuti)',
    lockoutThreshold: 'Blocco dopo tentativi falliti',
    lockoutDuration: 'Durata del blocco (minuti)',
    keyboardLayout: 'Layout tastiera',
    keyboardLayoutHint: 'es. it, en-us — vuoto per il default QEMU',
    syslogEndpoint: 'Endpoint syslog',
    syslogEndpointHint: "host:porta — vuoto per disabilitare l'inoltro",
    syslogTls: 'TLS',
    maxQueued: 'Massimo task in coda',
    concurrency: 'Concorrenza del worker',
    concurrencyHint: 'Applicata al volo dal worker',
    notesTitle: 'Note',
    save: 'Salva',
    saved: 'Impostazioni salvate',
    saveError: 'Impossibile salvare le impostazioni'
  },
  localSettings: {
    nodeSettingsTitle: 'Impostazioni nodo',
    nodeSettingsSubtitle: 'Ciò che questo nodo applica — coda dei task, console, inoltro log — più i suoi tag e le note',
    baseTitle: 'Riserve host (TVirt Proximity)',
    baseSubtitle: "CPU e RAM riservate al sistema operativo dell'host — gli input della capacità empirica con cui Proximity piazza le VM",
    baseMemory: 'RAM di base (MiB)',
    baseMemoryHint: 'Totale osservato: {total} MiB. Usabile per le VM = totale − base − RAM delle VM accese piazzate qui.',
    baseMemoryHintNoTotal: 'Usabile per le VM = totale osservato − base − RAM delle VM accese piazzate qui.',
    baseCpu: 'Core CPU di base',
    baseCpuHint: 'CPU logiche osservate: {total}. Usati solo come peso di ranking — l\'overcommit di vCPU è normale.',
    baseCpuHintNoTotal: "Usati solo come peso di ranking — l'overcommit di vCPU è normale.",
    baseHint: 'La RAM è il gate rigido di ammissione: una VM che non entra nella RAM usabile viene rifiutata con un errore parlante. Alza la base sugli host che ospitano membri dello store o demoni Ceph.',
    placementTitle: 'Posizionamento fisico',
    placementSubtitle: 'Dove sta fisicamente questo server — dichiarato una volta, qualunque cosa serva',
    placementHint: 'Il posizionamento guida la gerarchia CRUSH di ogni cluster Ceph con un OSD su questo server: salvare un cambio riallinea le loro mappe via task (host → rack → … → root). Le dichiarazioni parziali sono legali — i livelli vuoti collassano. I nomi sono condivisi su tutta la mappa: due server nello stesso rack dichiarano lo stesso nome di rack.',
    placementNameInvalid: 'Nome DNS-safe (minuscole, cifre, trattini)',
    placementEmpty: 'Vuoto = non dichiarato',
    placementLevel: {
      chassis: 'Chassis',
      rack: 'Rack',
      row: 'Fila',
      pdu: 'PDU',
      pod: 'Pod',
      room: 'Stanza',
      datacenter: 'Datacenter',
      zone: 'Zona',
      region: 'Regione'
    },
    cablingTitle: 'Cablaggio',
    cablingSubtitle: "A quale porta dello switch è collegata ogni interfaccia — la dichiarazione vive sul nodo (Annex O.9)",
    cablingHint: "Un cavo per NIC, un cavo per porta su tutta la flotta (l'API rifiuta nominando il titolare). Cablare verso una porta membro di un LAGG è legale — è il caso bond: la derivazione futura lo renderà come LACP.",
    cablingInterface: 'Interfaccia',
    cablingNotCabled: '— non collegata —',
    cablingNoInterfaces: 'Nessuna interfaccia di rete osservata',
    disksTitle: 'Dischi',
    disksSubtitle: 'Dispositivi a blocchi osservati — una scansione per passata di riconciliazione; contrassegna qui i dischi usabili come OSD Ceph (N.15: l\'eleggibilità è esplicita)',
    colDataDisk: 'Disco dati',
    dataDisksHint: 'Spuntare un dispositivo lo dichiara usabile come OSD; il seriale è il suo pin d\'identità — copiato dall\'hardware quando il disco lo riporta, coniato (tvirt-…) altrimenti. Il dialog di registrazione Ceph offre solo i dischi dichiarati.',
    diskDeclaredSerial: 'dichiarato',
    diskNotObserved: 'dichiarato ma non osservato — riaggancia il device o togli la spunta',
    noDisks: 'Nessun dispositivo a blocchi ancora osservato',
    colSerial: 'Seriale',
    colModel: 'Modello',
    colSize: 'Dimensione (GiB)',
    colDiskType: 'Tipo',
    colInUse: 'In uso',
    colObservedAt: 'Osservato',
    diskInUse: 'in uso',
    diskFree: 'libero',
    diskMounted: 'montato',
    diskPartitions: 'partizioni',
    passthroughTitle: 'Dispositivi passthrough',
    passthroughSubtitle: "GPU e dispositivi USB osservati — contrassegna qui cosa TVirt può passare alle VM; la marcatura registra l'identità del dispositivo (round U1+G1)",
    passthroughHint: "La marcatura registra il dispositivo (un id stabile — il target di tag e requisiti di classe). Una GPU marcata ha l'INTERO gruppo IOMMU legato a vfio-pci in blocco — lascia subito l'host; la smarcatura riaggancia il driver nativo. Per USB esistono entrambi i modelli ratificati: per dispositivo (vendor+prodotto, ovunque sia attaccato) e per porta fisica (alla VM arriva qualunque cosa ci sia infilata). L'USB non fa bind alla marcatura — l'attach avviene a runtime della VM.",
    passthroughGpusTitle: 'GPU / PCIe',
    passthroughUsbTitle: 'USB',
    colUsable: 'Usabile da TVirt',
    colGpu: 'GPU',
    colPciAddress: 'Indirizzo PCI',
    colDriver: 'Driver',
    colVerdict: 'Verdetto',
    gpuEligible: 'Idonea',
    gpuIneligible: 'Non idonea',
    gpuReason: {
      'no-iommu': "IOMMU non attivo sull'host",
      'group-spans-slots': 'gruppo IOMMU non isolato: si estende su altri slot',
      'multiple-display': 'gruppo IOMMU non isolato: più di una funzione video',
      'foreign-function': 'gruppo IOMMU non isolato: contiene una funzione estranea'
    },
    vfioNotReady: "L'IOMMU non è attivo su questo host: nessuna GPU è idonea finché la piattaforma host non lo abilita (ISO OEM) — TVirt non ritocca mai i kernel args a runtime.",
    vfioMissingKernelArg: "L'IOMMU non è attivo su questo host: il firmware riporta VT-d/AMD-Vi (tabella ACPI DMAR/IVRS presente) ma manca il kernel arg — aggiungi intel_iommu=on (Intel) o togli amd_iommu=off (AMD) dalla command line del kernel e riavvia. TVirt non ritocca mai i kernel args a runtime.",
    vfioFirmwareDisabled: "L'IOMMU non è attivo su questo host: VT-d/AMD-Vi è disabilitato nel firmware (nessuna tabella ACPI DMAR/IVRS) oppure la CPU non lo supporta — abilitalo nel setup del firmware, nessun kernel arg può accenderlo. TVirt non ritocca mai l'host a runtime.",
    gpuBindError: 'Bind vfio-pci fallito su {address}: {error}',

    // Firewall su bridge non gestiti (#168 §9-bis): la lista del consenso.
    firewallBridgesTitle: 'Firewall su bridge non gestiti',
    firewallBridgesSubtitle: 'I bridge costruiti a mano su questo nodo il cui setup acconsente alle chain firewall rese da TVirt sulle NIC guest attaccate',
    firewallBridgesHint: "TVirt non tocca mai il bridge estraneo — la chain àncora sul tap/veth, che è sempre di TVirt. La lista è un cancello di consenso: default-deny e anti-spoof possono rompere ciò che un setup manuale si aspetta, quindi una NIC con firewall su un bridge non gestito NON elencato qui viene rifiutata con un errore parlante.",
    firewallBridgesEmpty: 'Nessun bridge non gestito acconsente alle chain firewall rese da TVirt su questo nodo.',
    firewallBridgeAdd: 'Aggiungi bridge',
    firewallBridgeInvalid: 'Non è un nome di interfaccia Linux (1-15 caratteri, senza slash né spazi)',
    firewallBridgeDuplicate: 'Nome già in elenco',
    colMarkDevice: 'Per dispositivo',
    colMarkPort: 'Per porta',
    usbHub: 'hub',
    colUsbId: 'Vendor:Product',
    colPort: 'Porta',
    passthroughOrphan: 'orfano — registrato ma non più rilevato: ricollegalo o smarcalo',
    noGpus: 'Nessuna GPU ancora osservata',
    noUsb: 'Nessun dispositivo USB ancora osservato',
    networkGeneration: 'Generazione di rete',
    applied: 'Applicata',
    converged: 'allineato',
    behind: 'indietro — {m} / {n}',
    notConfigured: 'non configurato',
    rollbackBody: 'Applicazione della generazione {gen} fallita: {error}',
    rolledBackChip: 'rollback eseguito',
    nicsTitle: 'NIC',
    nicsSubtitle:
      "L'anagrafica delle NIC gestite — la registrazione È la dichiarazione di gestione: una NIC registrata senza configurazione non viene toccata (clear-if la dichiara esplicitamente pulita), un dispositivo non registrato non viene mai toccato",
    addNic: 'Registra NIC',
    addNicTitle: 'Registra NIC (add-nic)',
    delNic: 'Termina gestione',
    delNicTitle: 'Termina gestione (del-nic)',
    delNicConfirm:
      "La gestione di {name} termina: la voce d'anagrafica e la sua configurazione vengono eliminate e il profilo TVirt rimosso — il dispositivo resta come Linux lo ha.",
    nicName: 'Nome del dispositivo',
    nicMac: 'Indirizzo MAC permanente',
    nicMacHint: "il MAC hardware permanente — l'identità stabile fissata dal TVirt Server Installer",
    colObserved: 'Osservata',
    nicObserved: 'osservata',
    nicNotObserved: 'non osservata',
    nicMacMismatch: 'MAC divergente — osservato {mac}',
    noNics: 'Nessuna NIC registrata — registra un dispositivo per iniziare a gestirlo',
    noManagedDevices: "Nessun dispositivo gestito — registra prima una NIC, tutto si costruisce sull'anagrafica",
    inventoryTitle: 'Dispositivi osservati',
    inventorySubtitle: 'Aggiornati a ogni passata di riconciliazione dal worker del nodo — la validazione verifica i nomi dei dispositivi su questo inventario',
    colDevice: 'Dispositivo',
    colType: 'Tipo',
    colMac: 'MAC',
    colLink: 'Link',
    colSpeed: 'Velocità',
    colTopology: 'Topologia',
    colMtu: 'MTU',
    colAddresses: 'Indirizzi',
    linkUp: 'up',
    linkDown: 'down',
    lacpDegraded: 'LACP non aggrega',
    noInventory: 'Nessun dispositivo riportato finora',
    interfacesTitle: 'Interfacce',
    interfacesSubtitle:
      'Configurazione L3 delle NIC registrate e dei dispositivi logici dichiarati — set-if sostituisce il blocco intero, edit-if ritocca le proprietà, clear-if lo svuota (pulizia esplicita)',
    colConfig: 'Configurazione desiderata',
    clean: 'pulita',
    setIf: 'Modifica indirizzamento',
    setIfTitle: 'Modifica indirizzamento (set-if)',
    editIf: 'Modifica proprietà',
    editIfTitle: 'Modifica proprietà (edit-if)',
    clearIf: 'Svuota configurazione',
    clearIfTitle: 'Svuota configurazione (clear-if)',
    clearIfConfirm: "La configurazione dell'interfaccia viene svuotata. Un'interfaccia pulita è il prerequisito per asservirla come membro di bond/bridge o parent di VLAN.",
    ip4Mode: 'Modalità IPv4',
    ip6Mode: 'Modalità IPv6',
    modeUnconfigured: 'Non configurata',
    modeStatic: 'Statica',
    modeDhcp: 'DHCP',
    ip4Addresses: 'Indirizzi IPv4',
    ip6Addresses: 'Indirizzi IPv6',
    addressesHint: 'CIDR, separati da virgola',
    gw4: 'Gateway IPv4',
    gw6: 'Gateway IPv6',
    dns: 'Server DNS',
    dnsHint: 'separati da virgola — vuoto azzera il DNS (set-if rimpiazza il blocco)',
    routes4: 'Rotte statiche IPv4',
    routes6: 'Rotte statiche IPv6',
    routeTo: 'Destinazione (CIDR)',
    routeVia: 'Via',
    addRoute: 'Aggiungi rotta',
    routesChip: 'rotte',
    mtu: 'MTU',
    mtuHint: 'vuoto = default del driver',
    vlansTitle: 'VLAN',
    addVlan: 'Aggiungi VLAN',
    addVlanTitle: 'Aggiungi VLAN (add-vlan)',
    delVlanTitle: 'Elimina VLAN (del-vlan)',
    vlanParent: 'Interfaccia parent',
    vlanId: 'ID VLAN',
    vlanPreview: 'Nome del dispositivo',
    colParent: 'Parent',
    colVlanId: 'ID',
    noVlans: 'Nessuna VLAN dichiarata',
    bondsTitle: 'Bond',
    addBond: 'Aggiungi bond',
    addBondTitle: 'Aggiungi bond (add-bond)',
    editBondTitle: 'Modifica bond',
    editBond: 'Salva bond',
    delBondTitle: 'Elimina bond (del-bond)',
    bondName: 'Nome del bond',
    members: 'Membri',
    membersHint: 'i membri devono essere interfacce pulite (senza configurazione L3)',
    bondMode: 'Modalità',
    modeActiveBackup: 'active-backup',
    modeLacp: 'LACP (802.3ad)',
    miimon: 'miimon (ms)',
    primary: 'Membro primario',
    rate: 'Rate LACP',
    hash: 'Politica di hash in trasmissione',
    colMembers: 'Membri',
    colMode: 'Modalità',
    noBonds: 'Nessun bond dichiarato',
    bridgesTitle: 'Bridge',
    addBridge: 'Aggiungi bridge',
    addBridgeTitle: 'Aggiungi bridge (add-bridge)',
    editBridgeTitle: 'Modifica bridge',
    editBridge: 'Salva bridge',
    delBridgeTitle: 'Elimina bridge (del-bridge)',
    bridgeName: 'Nome del bridge',
    stp: 'STP',
    stpOn: 'STP attivo',
    stpOff: 'STP spento',
    colStp: 'STP',
    noBridges: 'Nessun bridge dichiarato',
    hostnameTitle: 'Hostname',
    hostname: 'Hostname',
    setHostname: 'Imposta hostname',
    setHostnameTitle: 'Imposta hostname (set-hostname)',
    hostnameHint: 'Solo identità OS — il nome del nodo nel cluster non viene toccato',
    notSet: 'non impostato',
    staticHostsTitle: 'Voci host statiche',
    addStaticHost: 'Aggiungi voce',
    addStaticHostTitle: 'Aggiungi voce host statica',
    delStaticHostTitle: 'Elimina voce host statica',
    hostIp: 'Indirizzo IP',
    hostNames: 'Nomi',
    hostNamesHint: 'separati da virgola o spazio',
    colIp: 'IP',
    colNames: 'Nomi',
    noStaticHosts: 'Nessuna voce host statica',
    delConfirm: 'Rimozione di {name} dal documento desiderato — il worker del nodo converge con un checkpoint NetworkManager e rollback automatico.',
    remove: 'Rimuovi',
    derivedChip: 'derivata',
    derivedTip: "Questo device è POSSEDUTO dalla derivazione della fabric (H2): l'editor rifiuta di toccarlo — modifica la fabric (o il cablaggio) e riapplica, oppure riprenditelo qui sotto.",
    takeOwnership: 'Prendi possesso',
    takeOwnershipTitle: 'Prendi possesso (take-ownership)',
    takeOwnershipConfirm: "{name} esce dal blocco fabric e torna un device MANUALE governato da questo editor. Il nodo diventa divergente, e il prossimo apply della derivazione lo rifiuterà (manualConflict) finché non lo rinomini o rimuovi — o correggi la fabric perché non sia più derivato.",
    opError: "L'operazione di rete è stata rifiutata"
  }
}
